import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached, getAccountsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteStockFlow } from '../../utils/stockFlowHelper';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import { ensureProductionWastageAccount, getLinkedAccount, createLinkedAccount } from '../../utils/coaInitializer';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import { isFutureDate } from '../../utils/validationHelpers';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import { inActiveBranch } from '../../utils/branchLink';

const TRAN_TYPE_CODE = 'PRC';

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted']
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitCost: 0, lineTotal: 0 };

// Overheads on a Production Receipt — mirrors the Production Order's model. Each
// overhead is allocated across the finished goods (raising their unit cost) and
// credited to its expense account (capitalized into inventory).
const EMPTY_OVERHEAD = { expenseType: 'Labour', accountId: '', accountCode: '', accountName: '', allocationMethod: 'Fixed', description: '', amount: 0 };
const OH_EXPENSE_TYPES = ['Labour', 'Factory Overhead', 'Utilities', 'Depreciation', 'Other'];
const OH_ALLOCATION_METHODS = ['Fixed', 'Per Unit', 'Percentage of Material'];

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
  Cancelled: colors.error.main,
};

const ProductionReceipt = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('store', 'productionReceipt');
  const location = useLocation();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [issues, setIssues] = useState([]); // posted Material Issues (MIP)
  // ALL PRC receipts (any year/page) — feeds the WIP-remaining math only.
  // The visible list is paginated below; this full set keeps the over-receipt
  // guard and the open-issues dropdown correct.
  const [receipts, setReceipts] = useState([]);
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: refreshList,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    issueId: '', issueDocNumber: '', notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  // Raw materials consumed on THIS receipt (rows built from the linked issue's materials).
  const [consumedRows, setConsumedRows] = useState([]);
  // Auto-cost the finished goods from the consumed material value (÷ finished qty).
  const [autoCost, setAutoCost] = useState(true);
  // Outsource processing charge (only when the linked issue is outsourced).
  const [outsourceCharge, setOutsourceCharge] = useState(0);
  const [overheadItems, setOverheadItems] = useState([]); // production overheads → divided into finished-goods cost
  const [expenseAccounts, setExpenseAccounts] = useState([]); // COA expense/liability accounts for the overhead picker
  const [vendorAccount, setVendorAccount] = useState(null); // linked COA account of the issue's vendor
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [printConfig, setPrintConfig] = useState(null);

  // The visible list loads inside useDocListPagination above (refreshList).
  // This loads ALL receipts for the WIP-remaining math (over-receipt guard).
  const loadReceipts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const data = await getTransactions(activeCompany.id, { tranTypeCode: TRAN_TYPE_CODE, ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {}) });
      setReceipts(data);
    } catch (error) {
      console.error('Error loading receipts:', error);
    }
  }, [activeCompany, activeBranch]);

  // Same name every existing call site uses — refreshes the paginated list
  // AND the full receipts set together.
  const loadRecords = useCallback(
    () => Promise.all([refreshList(), loadReceipts()]),
    [refreshList, loadReceipts]
  );

  const loadIssues = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const data = await getTransactions(activeCompany.id, { tranTypeCode: 'MIP' });
      setIssues(data.filter(i => i.status === 'Posted'));
    } catch (error) { console.error('Error loading issues:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
      // Expense/liability accounts (codes 5x/6x) for the overhead picker — cached, 0 extra reads.
      const accts = await getAccountsCached(activeCompany.id);
      setExpenseAccounts((accts || [])
        .filter(a => (a.level === 5 || a.isPostable) && (String(a.code || '').startsWith('5') || String(a.code || '').startsWith('6')))
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (error) { console.error('Error loading items:', error); }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // NOTE: the visible record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadReceipts(); loadIssues(); loadItems(); loadEmployees(); loadJobs(); loadLocations(); loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [loadReceipts, loadIssues, loadItems, loadEmployees, loadJobs, loadLocations]);

  // received-cost per issue (from all non-cancelled receipts EXCEPT the one being edited)
  const receivedByIssue = useMemo(() => {
    const map = {};
    receipts.forEach(r => {
      if (r.status === 'Cancelled') return;
      if (editingRecord && r.id === editingRecord.id) return;
      // INV-05: only the MATERIAL part of a receipt uses up WIP — overheads and
      // outsource charges are added on top, not drawn from the issue.
      const material = (Number(r.totalValue) || 0) - (Number(r.overheadValue) || 0) - (Number(r.outsourceCharge) || 0);
      if (r.parentId) map[r.parentId] = (map[r.parentId] || 0) + Math.max(0, material);
    });
    return map;
  }, [receipts, editingRecord]);

  // WIP pool remaining for the currently-selected issue
  const selectedIssue = useMemo(() => issues.find(i => i.id === formData.issueId) || null, [issues, formData.issueId]);
  const wipRemaining = useMemo(() => {
    if (!selectedIssue) return 0;
    const issued = Number(selectedIssue.totalValue) || 0;
    const received = receivedByIssue[selectedIssue.id] || 0;
    return Math.round((issued - received) * 100) / 100;
  }, [selectedIssue, receivedByIssue]);

  // Open issues (still have WIP remaining) of this branch (see utils/branchLink.js)
  // + always include the currently-linked one when editing
  const openIssues = useMemo(() => {
    return issues.filter(i => {
      const issued = Number(i.totalValue) || 0;
      const received = receivedByIssue[i.id] || 0;
      const remaining = issued - received;
      return (remaining > 0.01 && inActiveBranch(i, activeBranch)) || (editingRecord && editingRecord.parentId === i.id);
    });
  }, [issues, receivedByIssue, editingRecord, activeBranch]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.notes?.toLowerCase().includes(q) || r.issueDocNumber?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({
    ...line,
    unitPrice: line.unitCost || 0,
    lineTotal: Math.round(line.quantity * line.unitCost * 100) / 100
  });

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    return { totalValue: Math.round(lines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 };
  }, [lineItems]);

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Per issue → per material → {consumed, wasted} summed across all OTHER
  // non-cancelled receipts (excludes the one being edited). Quantity-based, from
  // the already-loaded receipts (no extra Firebase reads). Old receipts saved
  // before this feature have no consumedMaterials and simply contribute nothing.
  const priorAccountedByIssueItem = useMemo(() => {
    const map = {};
    receipts.forEach(r => {
      if (r.status === 'Cancelled') return;
      if (editingRecord && r.id === editingRecord.id) return;
      if (!r.parentId || !Array.isArray(r.consumedMaterials)) return;
      const bucket = map[r.parentId] || (map[r.parentId] = {});
      r.consumedMaterials.forEach(cm => {
        const b = bucket[cm.itemId] || (bucket[cm.itemId] = { consumed: 0, wasted: 0 });
        b.consumed += Number(cm.quantity) || 0;
        b.wasted += Number(cm.wastage) || 0;
      });
    });
    return map;
  }, [receipts, editingRecord]);

  // Value of materials consumed / wasted on THIS receipt.
  const consumedTotals = useMemo(() => {
    let consumedValue = 0, wastageValue = 0;
    consumedRows.forEach(r => {
      consumedValue += (Number(r.quantity) || 0) * (Number(r.unitCost) || 0);
      wastageValue += (Number(r.wastage) || 0) * (Number(r.unitCost) || 0);
    });
    return { consumedValue: round2(consumedValue), wastageValue: round2(wastageValue) };
  }, [consumedRows]);

  const totalFgQty = useMemo(() => lineItems.reduce((s, l) => s + (Number(l.quantity) || 0), 0), [lineItems]);

  // Outsource processing charge, only counted when the linked issue is outsourced.
  const isOutsource = selectedIssue?.productionType === 'outsource';
  const chargeValue = useMemo(() => (isOutsource ? round2(outsourceCharge) : 0), [isOutsource, outsourceCharge]);

  // Overheads → divided into finished-goods cost. Allocation: Fixed (whole amount),
  // Per Unit (× finished qty), or Percentage of Material (% of consumed material value).
  const overheadTotals = useMemo(() => {
    const matCost = consumedTotals.consumedValue;
    const qty = totalFgQty || 0;
    let total = 0;
    const lines = overheadItems.map(oh => {
      const amt = Number(oh.amount) || 0;
      let eff = amt;
      if (oh.allocationMethod === 'Per Unit') eff = amt * qty;
      else if (oh.allocationMethod === 'Percentage of Material') eff = matCost * amt / 100;
      eff = round2(eff);
      total += eff;
      return { ...oh, effectiveAmount: eff };
    });
    return { total: round2(total), lines };
  }, [overheadItems, consumedTotals.consumedValue, totalFgQty]);

  // Auto-cost: finished-good unit cost = (consumed material value + processing charge
  // + overheads) ÷ total finished qty. The `changed` guard keeps this from looping.
  useEffect(() => {
    if (!autoCost || totalFgQty <= 0) return;
    const perUnit = round2((consumedTotals.consumedValue + chargeValue + overheadTotals.total) / totalFgQty);
    setLineItems(prev => {
      let changed = false;
      const next = prev.map(l => {
        if (round2(l.unitCost) !== perUnit) { changed = true; return calculateLine({ ...l, unitCost: perUnit }); }
        return l;
      });
      return changed ? next : prev;
    });
  }, [autoCost, consumedTotals.consumedValue, chargeValue, overheadTotals.total, totalFgQty]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addOverhead = () => setOverheadItems(prev => [...prev, { ...EMPTY_OVERHEAD }]);
  const removeOverhead = (index) => setOverheadItems(prev => prev.filter((_, i) => i !== index));
  const handleOverheadChange = (index, field, value) => {
    setOverheadItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'accountId' && value) {
        const acc = expenseAccounts.find(a => a.id === value);
        if (acc) { updated[index].accountCode = acc.code || ''; updated[index].accountName = acc.name || ''; }
      }
      return updated;
    });
  };

  const handleIssueChange = (issueId) => {
    const issue = issues.find(i => i.id === issueId);
    setFormData(prev => ({ ...prev, issueId, issueDocNumber: issue?.docNumber || '' }));
    // Reset + resolve the outsource vendor's payable account for the charge leg.
    setOutsourceCharge(0);
    setVendorAccount(null);
    if (issue?.productionType === 'outsource' && issue.vendorId) {
      getLinkedAccount(activeCompany.id, 'vendor', issue.vendorId).then(setVendorAccount).catch(() => setVendorAccount(null));
    }
    // Build the raw-material rows from the issue's material lines, showing how much
    // of each was already consumed/wasted by earlier receipts against this issue.
    const prior = priorAccountedByIssueItem[issueId] || {};
    const rows = (issue?.lines || []).map(l => {
      const acc = prior[l.itemId] || { consumed: 0, wasted: 0 };
      return {
        itemId: l.itemId,
        itemName: l.itemName || l.description || '',
        itemCode: l.itemCode || '',
        unitOfMeasure: l.unitOfMeasure || l.unitCode || '',
        unitCost: Number(l.unitCost ?? l.unitPrice) || 0,
        issuedQty: Number(l.quantity) || 0,
        priorAccounted: round2((acc.consumed || 0) + (acc.wasted || 0)),
        quantity: 0, // consumed on this receipt
        wastage: 0   // wasted on this receipt
      };
    });
    setConsumedRows(rows);
  };

  // Edit a consumed-material row; caps consumed + wastage to what's still available.
  const handleConsumedChange = (index, field, value) => {
    setConsumedRows(prev => {
      const next = [...prev];
      const row = { ...next[index] };
      let v = Number(value) || 0;
      if (v < 0) v = 0;
      const available = round2((row.issuedQty || 0) - (row.priorAccounted || 0));
      const other = field === 'quantity' ? (Number(row.wastage) || 0) : (Number(row.quantity) || 0);
      if (v + other > available) v = Math.max(0, round2(available - other));
      row[field] = v;
      next[index] = row;
      return next;
    });
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          // keep the user's manual cost; only sync unit label/code
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure };
        }
      }
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          // unit only — cost is entered manually for finished goods
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure };
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => { const el = lineItemRefs.current[rowIdx]; if (el && typeof el.focus === 'function') el.focus(); }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      if (window.confirm('Add a new line item?')) { pendingFocusRowRef.current = lineItems.length; addLine(); }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], issueId: '', issueDocNumber: '', notes: '', status: 'Draft', employeeId: '', employeeName: '', jobId: '', jobName: '', locationId: '', locationName: '' });
    setLineItems([{ ...EMPTY_LINE }]);
    setConsumedRows([]);
    setAutoCost(true);
    setOutsourceCharge(0);
    setOverheadItems([]);
    setVendorAccount(null);
    setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], issueId: rec.parentId || '', issueDocNumber: rec.issueDocNumber || '', notes: rec.notes || '', status: rec.status || 'Draft', employeeId: rec.employeeId || '', employeeName: rec.employeeName || '', jobId: rec.jobId || '', jobName: rec.jobName || '', locationId: rec.locationId || '', locationName: rec.locationName || '' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setAutoCost(rec.autoCost !== false);
    setOutsourceCharge(Number(rec.outsourceCharge) || 0);
    setOverheadItems(Array.isArray(rec.overheads) ? rec.overheads.map(o => ({ ...EMPTY_OVERHEAD, ...o, amount: Number(o.amount) || 0 })) : []);
    setVendorAccount(null);
    // Rebuild the consumed-material rows: issue materials + prior receipts (excluding
    // THIS one) for the "already accounted" figures, overlaid with this receipt's saved consumption.
    const issue = issues.find(i => i.id === rec.parentId);
    if (issue?.productionType === 'outsource' && issue.vendorId) {
      getLinkedAccount(activeCompany.id, 'vendor', issue.vendorId).then(setVendorAccount).catch(() => setVendorAccount(null));
    }
    const prior = {};
    receipts.forEach(r => {
      if (r.status === 'Cancelled' || r.id === rec.id) return;
      if (r.parentId !== rec.parentId || !Array.isArray(r.consumedMaterials)) return;
      r.consumedMaterials.forEach(cm => {
        const b = prior[cm.itemId] || (prior[cm.itemId] = { consumed: 0, wasted: 0 });
        b.consumed += Number(cm.quantity) || 0;
        b.wasted += Number(cm.wastage) || 0;
      });
    });
    const savedByItem = {};
    (rec.consumedMaterials || []).forEach(cm => { savedByItem[cm.itemId] = cm; });
    const rows = (issue?.lines || []).map(l => {
      const acc = prior[l.itemId] || { consumed: 0, wasted: 0 };
      const saved = savedByItem[l.itemId];
      return {
        itemId: l.itemId,
        itemName: l.itemName || l.description || '',
        itemCode: l.itemCode || '',
        unitOfMeasure: l.unitOfMeasure || l.unitCode || '',
        unitCost: Number(l.unitCost ?? l.unitPrice) || 0,
        issuedQty: Number(l.quantity) || 0,
        priorAccounted: round2((acc.consumed || 0) + (acc.wasted || 0)),
        quantity: saved ? (Number(saved.quantity) || 0) : 0,
        wastage: saved ? (Number(saved.wastage) || 0) : 0
      };
    });
    setConsumedRows(rows);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports. With pagination the target may not be in
  // the loaded page — ensureDoc fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        setViewingRecord(rec);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }
    if (!formData.issueId) { alert('Please link this receipt to a Material Issue'); return; }
    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one finished good with quantity'); return; }
    if (validLines.some(l => Number(l.unitCost) <= 0)) {
      if (!window.confirm('One or more finished goods have zero cost. Continue?')) return;
    }

    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Over-receipt warning: receipt cost should not exceed remaining WIP pool.
    if (saveStatus === 'Posted' && totals.totalValue > wipRemaining + 0.01) {
      if (!window.confirm(`This receipt's cost (${formatAmount(totals.totalValue)}) exceeds the remaining material cost in production (${formatAmount(wipRemaining)}) for ${formData.issueDocNumber}.\n\nThis will leave a negative WIP balance. Continue?`)) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      const status = saveStatus || 'Draft';

      // Resolve the outsource vendor's ledger account SYNCHRONOUSLY here, so the
      // charge always posts to the vendor regardless of the async lookup's timing
      // or of draft/post. It gets stored on the doc; the posting engine reads it
      // from there (the account cache only holds system accounts, not vendors).
      let resolvedVendorAcc = vendorAccount;
      if (isOutsource && chargeValue > 0 && selectedIssue?.vendorId) {
        if (!resolvedVendorAcc?.id) {
          resolvedVendorAcc = await getLinkedAccount(activeCompany.id, 'vendor', selectedIssue.vendorId);
        }
        // Vendor has no ledger account yet — create its payable account (same one
        // the Vendors screen makes) so the outsource charge always has somewhere to post.
        if (!resolvedVendorAcc?.id) {
          const created = await createLinkedAccount(activeCompany.id, 'vendor', selectedIssue.vendorId, selectedIssue.vendorName || 'Vendor', currentUser.uid);
          if (created?.success && created.accountId) {
            resolvedVendorAcc = { id: created.accountId, code: created.accountCode, name: `Vendor: ${selectedIssue.vendorName || ''}`.trim() };
          }
        }
        if (resolvedVendorAcc?.id) setVendorAccount(resolvedVendorAcc);
      }
      if (status === 'Posted' && chargeValue > 0) {
        if (chargeValue > totals.totalValue + 0.01) {
          alert('The finished-goods value must be at least the outsource charge. Turn on "Auto-cost from materials" or raise the finished-goods cost.');
          setSaving(false); return;
        }
        if (!resolvedVendorAcc?.id) {
          alert("Could not create the outsource vendor's ledger account. Please check the vendor under Purchases → Vendors, then try again.");
          setSaving(false); return;
        }
      }

      // Raw materials consumed / wasted on this receipt (only rows that used something).
      const consumedMaterials = consumedRows
        .filter(r => (Number(r.quantity) || 0) > 0 || (Number(r.wastage) || 0) > 0)
        .map(r => ({
          itemId: r.itemId, itemName: r.itemName, itemCode: r.itemCode,
          unitOfMeasure: r.unitOfMeasure, unitCost: Number(r.unitCost) || 0,
          quantity: Number(r.quantity) || 0, wastage: Number(r.wastage) || 0,
          consumedCost: round2((Number(r.quantity) || 0) * (Number(r.unitCost) || 0)),
          wastageCost: round2((Number(r.wastage) || 0) * (Number(r.unitCost) || 0))
        }));
      const data = {
        tranTypeCode: TRAN_TYPE_CODE,
        financialYearId: activeFinancialYear.id,
        docDate: formData.docDate, notes: formData.notes, status,
        parentId: formData.issueId,                 // links to the Material Issue (MIP)
        issueDocNumber: formData.issueDocNumber,
        employeeId: formData.employeeId, employeeName: formData.employeeName,
        jobId: formData.jobId, jobName: formData.jobName,
        locationId: formData.locationId, locationName: formData.locationName,
        totalValue: totals.totalValue, lines: calculatedLines,
        // Manual-production consumption tracking (no BOM): user-entered per material.
        consumedMaterials,
        consumedValue: consumedTotals.consumedValue,
        wastageValue: consumedTotals.wastageValue,
        autoCost,
        // Production overheads → divided into FG cost + credited to their expense accounts.
        overheads: overheadTotals.lines
          .filter(o => o.accountId && (Number(o.effectiveAmount) || 0) > 0)
          .map(o => ({ expenseType: o.expenseType, accountId: o.accountId, accountCode: o.accountCode || '', accountName: o.accountName || '', allocationMethod: o.allocationMethod, description: o.description || '', amount: Number(o.amount) || 0, effectiveAmount: Number(o.effectiveAmount) || 0 })),
        overheadValue: overheadTotals.total,
        // Outsource processing charge → credited to the vendor, folded into FG cost.
        outsourceCharge: chargeValue,
        outsourceVendorId: isOutsource ? (selectedIssue?.vendorId || '') : '',
        outsourceVendorName: isOutsource ? (selectedIssue?.vendorName || '') : '',
        outsourceVendorAccountId: chargeValue > 0 ? (resolvedVendorAcc?.id || null) : null,
        outsourceVendorAccountCode: chargeValue > 0 ? (resolvedVendorAcc?.code || null) : null,
        outsourceVendorAccountName: chargeValue > 0 ? (resolvedVendorAcc?.name || (selectedIssue?.vendorName ? `Vendor: ${selectedIssue.vendorName}` : 'Outsource Vendor')) : null,
        branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null
      };

      // Make sure the Production Wastage account exists before posting a receipt
      // that writes off wastage, so the write-off leg always has an account to hit.
      if (status === 'Posted' && consumedTotals.wastageValue > 0) {
        await ensureProductionWastageAccount(activeCompany.id, currentUser.uid);
      }

      let savedDocId = editingRecord?.id;
      let newTranData = null;
      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        savedDocId = newTranData.id;
      }

      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();
      Promise.all([loadRecords(), loadIssues()]).catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await Promise.all([loadRecords(), loadIssues()]);
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to cancel.'); return; }
    if (rec.status === 'Cancelled') { alert('Already cancelled'); return; }
    if (rec.status === 'Posted') {
      if (!window.confirm('This will reverse the stock and accounting (WIP) entries. Continue?')) return;
    }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Posted') {
        await deleteStockFlow(activeCompany.id, rec.id);
        await deleteSubTransactions(activeCompany.id, rec.id);
      }
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await Promise.all([loadRecords(), loadIssues()]);
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('productionReceipt', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Production Receipt</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Production Receipts</h3>
          <p>{records.length === 0 ? 'Receive finished goods back from production to get started' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Production Receipt</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="issueDocNumber">From Issue</SortableTh><SortableTh field="locationName">Location</SortableTh>
          <SortableTh field="totalValue" style={{ textAlign: 'right' }}>FG Cost</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.issueDocNumber || '-'}</Td>
              <Td>{rec.locationName || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}><FiXCircle /></IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Posted: <strong>{listSummary.counts.Posted || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Receipt Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>From Material Issue</label><span>{rec.issueDocNumber || '-'}</span></InfoItem>
            <InfoItem><label>Into Location</label><span>{rec.locationName || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Finished Goods Cost</label><span>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</span></InfoItem>
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Finished Goods Received</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th>
            <Th style={{ textAlign: 'right' }}>Unit Cost</Th><Th style={{ textAlign: 'right' }}>Total</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td>
                <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost)}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Total Finished Goods Cost ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalValue)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {Array.isArray(rec.consumedMaterials) && rec.consumedMaterials.length > 0 && (
          <FormCard>
            <SectionTitle>Raw Materials Consumed</SectionTitle>
            <TableContainer><Table><thead><tr>
              <Th>Material</Th>
              <Th style={{ textAlign: 'right' }}>Consumed</Th>
              <Th style={{ textAlign: 'right' }}>Wastage</Th>
              <Th style={{ textAlign: 'right' }}>Unit Cost</Th>
              <Th style={{ textAlign: 'right' }}>Consumed Value</Th>
            </tr></thead><tbody>
              {rec.consumedMaterials.map((cm, i) => (
                <Tr key={cm.itemId || i}>
                  <Td>{cm.itemCode ? <span style={{ color: colors.primary.main, marginRight: 6, fontSize: 12 }}>{cm.itemCode}</span> : null}{cm.itemName}</Td>
                  <Td style={{ textAlign: 'right' }}>{cm.quantity}{cm.unitOfMeasure ? ` ${cm.unitOfMeasure}` : ''}</Td>
                  <Td style={{ textAlign: 'right', color: (cm.wastage || 0) > 0 ? colors.error.main : undefined }}>{cm.wastage || 0}{cm.unitOfMeasure ? ` ${cm.unitOfMeasure}` : ''}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(cm.unitCost)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(cm.consumedCost)}</Td>
                </Tr>
              ))}
            </tbody></Table></TableContainer>
            <TotalsSection><TotalsTable><tbody>
              <TotalRow><td>Material consumed value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.consumedValue)}</td></TotalRow>
              {(rec.wastageValue || 0) > 0 && <tr><td>Wastage written off ({activeCompany?.currencyCode || 'PKR'})</td><td style={{ color: colors.error.main }}>{formatAmount(rec.wastageValue)}</td></tr>}
              {(rec.outsourceCharge || 0) > 0 && <tr><td>Outsource charge — {rec.outsourceVendorName || 'vendor'} ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.outsourceCharge)}</td></tr>}
            </tbody></TotalsTable></TotalsSection>
          </FormCard>
        )}
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Production Receipt'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}>
              <FiCheck /> {editingRecord && formData.status === 'Posted' ? 'Re-Post' : 'Save & Post'}
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Receipt Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Link to Material Issue *</Label>
            <Select value={formData.issueId} onChange={e => handleIssueChange(e.target.value)}>
              <option value="">-- Select Material Issue --</option>
              {openIssues.map(iss => {
                const issued = Number(iss.totalValue) || 0;
                const remaining = issued - (receivedByIssue[iss.id] || 0);
                return <option key={iss.id} value={iss.id}>{iss.docNumber} — {iss.docDate} (WIP left: {formatAmount(remaining)})</option>;
              })}
            </Select>
          </FormGroup>
          <FormGroup><Label>{reqLabel(activeCompany, 'Into Location')}</Label>
            <Select value={formData.locationId} onChange={e => { const loc = locations.find(l => l.id === e.target.value); handleHeaderChange('locationId', e.target.value); handleHeaderChange('locationName', loc ? (loc.name || loc.locationName || '') : ''); }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name || loc.locationName || loc.id}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => { const emp = employees.find(em => em.id === e.target.value); handleHeaderChange('employeeId', e.target.value); handleHeaderChange('employeeName', emp ? (emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || '') : ''); }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || emp.id}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Job</Label>
            <Select value={formData.jobId} onChange={e => { const job = jobs.find(j => j.id === e.target.value); handleHeaderChange('jobId', e.target.value); handleHeaderChange('jobName', job ? (job.name || job.jobName || '') : ''); }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => <option key={job.id} value={job.id}>{job.name || job.jobName || job.id}</option>)}
            </Select>
          </FormGroup>
        </FormGrid>
        {selectedIssue && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: colors.primary.main + '10', border: `1px solid ${colors.primary.main}30`, borderRadius: 8, fontSize: 13, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <span>Material issued: <strong>{activeCompany?.currencyCode || 'PKR'} {formatAmount(Number(selectedIssue.totalValue) || 0)}</strong></span>
            <span>Remaining in production (WIP): <strong style={{ color: wipRemaining < 0 ? colors.error.main : colors.success.main }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(wipRemaining)}</strong></span>
            <span>This receipt: <strong>{activeCompany?.currencyCode || 'PKR'} {formatAmount(totals.totalValue)}</strong></span>
          </div>
        )}
      </FormCard>
      {formData.issueId && consumedRows.length > 0 && (
        <FormCard>
          <SectionTitle>Raw Materials Consumed</SectionTitle>
          <p style={{ fontSize: 12, color: colors.text.secondary, marginTop: -6, marginBottom: 12 }}>
            Enter how much of each issued material this receipt used, and any wastage. Available = Issued − already used by earlier receipts − this receipt.
          </p>
          <LineTableContainer><LineTable><thead><tr>
            <LineTh>Material</LineTh>
            <LineTh style={{ textAlign: 'right', width: 120 }}>Issued</LineTh>
            <LineTh style={{ textAlign: 'right', width: 130 }}>Already Used</LineTh>
            <LineTh style={{ textAlign: 'right', width: 140 }}>Consumed (this)</LineTh>
            <LineTh style={{ textAlign: 'right', width: 130 }}>Wastage (this)</LineTh>
            <LineTh style={{ textAlign: 'right', width: 120 }}>Available</LineTh>
          </tr></thead><tbody>
            {consumedRows.map((r, i) => {
              const available = round2((r.issuedQty || 0) - (r.priorAccounted || 0) - (Number(r.quantity) || 0) - (Number(r.wastage) || 0));
              const u = r.unitOfMeasure ? ` ${r.unitOfMeasure}` : '';
              return (
                <tr key={r.itemId || i}>
                  <LineTd>{r.itemCode ? <span style={{ color: colors.primary.main, marginRight: 6, fontSize: 12 }}>{r.itemCode}</span> : null}{r.itemName}</LineTd>
                  <LineTd style={{ textAlign: 'right' }}>{formatAmount(r.issuedQty)}{u}</LineTd>
                  <LineTd style={{ textAlign: 'right', color: colors.text.secondary }}>{formatAmount(r.priorAccounted)}{u}</LineTd>
                  <LineTd><LineInput type="number" min="0" step="0.01" value={r.quantity} onChange={e => handleConsumedChange(i, 'quantity', e.target.value)} style={{ textAlign: 'right' }} /></LineTd>
                  <LineTd><LineInput type="number" min="0" step="0.01" value={r.wastage} onChange={e => handleConsumedChange(i, 'wastage', e.target.value)} style={{ textAlign: 'right' }} /></LineTd>
                  <LineTd style={{ textAlign: 'right', fontWeight: 600, color: available <= 0.0001 ? colors.success.main : colors.text.primary }}>{formatAmount(available)}{u}</LineTd>
                </tr>
              );
            })}
          </tbody></LineTable></LineTableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Material consumed value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(consumedTotals.consumedValue)}</td></TotalRow>
            {consumedTotals.wastageValue > 0 && <tr><td>Wastage written off ({activeCompany?.currencyCode || 'PKR'})</td><td style={{ color: colors.error.main }}>{formatAmount(consumedTotals.wastageValue)}</td></tr>}
          </tbody></TotalsTable></TotalsSection>
          {isOutsource && (
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#FFF8E1', border: '1px solid #F0D98C', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13 }}><strong>Outsourced to:</strong> {selectedIssue?.vendorName || '-'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Label style={{ margin: 0 }}>Processing Charge ({activeCompany?.currencyCode || 'PKR'})</Label>
                  <LineInput type="number" min="0" step="0.01" value={outsourceCharge} onChange={e => setOutsourceCharge(Number(e.target.value) || 0)} style={{ textAlign: 'right', width: 140 }} />
                </div>
              </div>
              <div style={{ fontSize: 12, color: colors.text.secondary, marginTop: 6 }}>
                Credited to the vendor's account (payable) and added into the finished-goods cost.
                {chargeValue > 0 && !vendorAccount && <span style={{ color: colors.error.main }}> Vendor account not found — open this vendor once under Vendors.</span>}
              </div>
            </div>
          )}
        </FormCard>
      )}
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Overheads (divided into finished-goods cost)</SectionTitle>
          <AddLineButton onClick={addOverhead}><FiPlus /> Add Overhead</AddLineButton>
        </LineItemsHeader>
        {overheadItems.length === 0 ? (
          <div style={{ padding: '10px 4px', fontSize: 12, color: colors.text.secondary }}>No overheads. Add labour / factory overhead / utilities etc. to include them in the finished-goods cost.</div>
        ) : (
          <LineTableContainer><LineTable><thead><tr>
            <LineTh style={{ width: 40, minWidth: 40, maxWidth: 40 }}>#</LineTh>
            <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Type</LineTh>
            <LineTh style={{ width: 240, minWidth: 240, maxWidth: 240 }}>Account</LineTh>
            <LineTh style={{ width: 170, minWidth: 170, maxWidth: 170 }}>Allocation</LineTh>
            <LineTh style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Amount</LineTh>
            <LineTh style={{ width: 120, textAlign: 'right' }}>Total</LineTh>
            <LineTh style={{ width: 40, minWidth: 40, maxWidth: 40 }}></LineTh>
          </tr></thead><tbody>
            {overheadTotals.lines.map((oh, index) => (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd>
                  <Select value={oh.expenseType} onChange={e => handleOverheadChange(index, 'expenseType', e.target.value)}>
                    {OH_EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </LineTd>
                <LineTd>
                  <Select value={oh.accountId} onChange={e => handleOverheadChange(index, 'accountId', e.target.value)}>
                    <option value="">-- Account --</option>
                    {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                  </Select>
                </LineTd>
                <LineTd>
                  <Select value={oh.allocationMethod} onChange={e => handleOverheadChange(index, 'allocationMethod', e.target.value)}>
                    {OH_ALLOCATION_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </Select>
                </LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={oh.amount} onChange={e => handleOverheadChange(index, 'amount', Number(e.target.value))} style={{ textAlign: 'right' }} title={oh.allocationMethod === 'Per Unit' ? 'Amount per finished unit' : oh.allocationMethod === 'Percentage of Material' ? '% of material cost' : 'Fixed total amount'} /></LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(oh.effectiveAmount || 0)}</LineTd>
                <LineTd style={{ textAlign: 'center' }}><RemoveLineBtn onClick={() => removeOverhead(index)}><FiX /></RemoveLineBtn></LineTd>
              </tr>
            ))}
          </tbody></LineTable></LineTableContainer>
        )}
        {overheadTotals.total > 0 && (
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Total overheads → finished goods ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(overheadTotals.total)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        )}
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Finished Goods</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text.secondary, cursor: 'pointer' }}>
              <input type="checkbox" checked={autoCost} onChange={e => setAutoCost(e.target.checked)} />
              Auto-cost from materials
            </label>
            <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
          </div>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
          <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Cost</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            return (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} /></LineTd>
                <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
                <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} disabled={autoCost} title={autoCost ? 'Auto-calculated from materials consumed' : undefined} style={{ textAlign: 'right', background: autoCost ? colors.neutral[50] : undefined }} /></LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
              </tr>
            );
          })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <TotalRow><td>Total Finished Goods Cost ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalValue)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode, saving
  });

  return (
    <PageLayout pageTitle="Production Receipt" breadcrumb="Store / Production Receipt">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default ProductionReceipt;
